/**
 * Çok dilli (6 dil). Varsayılan EN (spec: trafik ABD/UK — bkz. config.ts DEFAULT_LANG).
 * Diğerleri: TR/FR/DE/RU/AR (AR = RTL).
 * Metin + metrik etiketleri tek yerde. metric_<key> = analiz metriklerinin görünen adı.
 * NOT: RU ve AR çevirileri taslaktır — native gözden geçirme önerilir.
 */
import { DEFAULT_LANG } from "./config";

export type Lang = "tr" | "en" | "fr" | "de" | "ru" | "ar";

export const LANGS: { code: Lang; name: string; rtl?: boolean }[] = [
  { code: "tr", name: "Türkçe" },
  { code: "en", name: "English" },
  { code: "fr", name: "Français" },
  { code: "de", name: "Deutsch" },
  { code: "ru", name: "Русский" },
  { code: "ar", name: "العربية", rtl: true },
];

type Dict = Record<string, string>;

const TR: Dict = {
  app_title: "Gülüş Değerlendirmesi",
  app_subtitle: "Cihazınızda çalışır. Görüntüleriniz hiçbir yere yüklenmez.",
  privacy_badge: "🔒 Verileriniz cihazınızda işleniyor, sunucuya gönderilmiyor",
  consent_title: "Başlamadan önce",
  consent_body:
    "Yüzünüz 3 açıdan, tamamen cihazınızda analiz edilir; görüntüler yüklenmez. Tıbbi teşhis değildir.",
  consent_accept: "Anladım, başla",
  start_camera: "Kamerayı aç",
  step_label: "Adım",
  capture_btn: "Çek",
  retake_btn: "Tekrar çek",
  next_btn: "Devam",
  hint_frontal: "Yüzünüzü tam karşıya, kameraya bakacak şekilde hizalayın.",
  hint_left_three_quarter: "Başınızı hafifçe sola çevirin (~45°).",
  hint_right_three_quarter: "Başınızı hafifçe sağa çevirin (~45°).",
  warn_turn_left: "Biraz sola dönün",
  warn_turn_right: "Biraz sağa dönün",
  warn_chin_down: "Çenenizi hafif indirin",
  warn_chin_up: "Çenenizi hafif kaldırın",
  warn_straighten: "Başınızı dikleştirin",
  warn_blur: "Görüntü bulanık, sabit tutun",
  warn_no_face: "Yüz algılanamadı, çerçeveye girin",
  ok_aligned: "Hizalandı ✓",
  loading_model: "Yüz analiz modeli yükleniyor…",
  model_error: "Analiz modeli yüklenemedi. İnternet bağlantınızı kontrol edin.",
  done_body: "3/3 kare alındı.",
  done_title: "Ön-değerlendirme sonucu",
  analysis_overall: "Genel uyum",
  analysis_failed: "Analiz için net bir frontal kare gerekli. Lütfen tekrar deneyin.",
  analysis_note: "Skorlar estetik referans aralıklarına göre ortalamaya yakınlığı gösterir; 'normal/anormal' değildir.",
  col_metric: "Ölçüm",
  col_value: "Değer",
  col_ref: "Referans",
  col_score: "Skor",
  section_geometry: "Yüz oranı / geometri",
  section_skin: "Cilt yüzey (heuristik)",
  download_pdf: "PDF rapor indir",
  lead_title: "Uzman görüşü ister misiniz?",
  lead_sub: "Bırakın, Nexwell ekibi size WhatsApp'tan ulaşsın. Görüntüleriniz gönderilmez; yalnızca skor özeti paylaşılır.",
  lead_name: "Ad Soyad",
  lead_phone: "Telefon (WhatsApp)",
  lead_email: "E-posta",
  lead_consent: "Kişisel verilerimin (ad, telefon, e-posta, skor özeti) Nexwell tarafından iletişim amacıyla işlenmesine KVKK kapsamında açık rıza veriyorum.",
  lead_submit: "Gönder",
  lead_sending: "Gönderiliyor…",
  lead_ok: "Teşekkürler! En kısa sürede size ulaşacağız.",
  lead_consent_required: "Göndermek için KVKK onayı gerekli.",
  lead_no_webhook: "Form şu an yapılandırılmadı (webhook tanımlı değil).",
  lead_error: "Gönderilemedi, lütfen tekrar deneyin.",
  lead_country: "Ülke",
  gate_ready: "Ön-değerlendirmeniz hazır",
  gate_title: "Sonucunuzu görmek için bilgilerinizi girin",
  gate_sub: "Raporunuzu görmek için iletişim bilgilerinizi bırakın. Nexwell uzmanları size WhatsApp'tan ulaşır. Görüntüleriniz gönderilmez; yalnızca skor özeti paylaşılır.",
  gate_submit: "Sonucu göster",
  gate_fill_required: "Lütfen tüm alanları doğru doldurun.",
  teaser_title: "İşte ön-değerlendirme skorunuz",
  teaser_sub: "Yüz oranlarınız ve cilt yüzeyiniz cihazınızda analiz edildi. İşte genel sonucunuz:",
  teaser_overall_cap: "Genel uyum skoru",
  teaser_highlight_intro: "Öne çıkan ölçümleriniz",
  teaser_cta: "Detaylı raporu aç",
  teaser_locked_hint: "Tüm metrik dökümü, görsel rapor, PDF indirme ve kişisel iyileştirme önerileri tek adım uzakta.",
  report_tips_title: "Nasıl iyileştirilir?",
  report_tips_intro: "Aşağıdaki öneriler genel bilgilendirmedir; kişiye özel plan için Nexwell uzmanına danışın.",
  legal_disclaimer:
    "Bu değerlendirme bilgilendirme amaçlıdır, tıbbi teşhis veya tedavi önerisi değildir. Kesin değerlendirme için hekime başvurun.",
  camera_error: "Kameraya erişilemedi. Lütfen kamera iznini verin ve HTTPS bağlantısı kullanın.",
  camera_error_demo_cta: "Kamera olmadan örnek raporu görüntüle",
  optional_suffix: " (opsiyonel)",
  turnstile_verifying: "Doğrulanıyor…",
  turnstile_error: "Doğrulama başarısız oldu, lütfen tekrar deneyin.",
  metric_faceLengthToWidth: "Yüz boyu/eni",
  metric_mouthToNoseWidth: "Ağız/burun eni",
  metric_upperToLowerLip: "Üst/alt dudak",
  metric_intercanthalRatio: "Göz aralığı dengesi",
  metric_facialThirds: "Üçler dengesi",
  metric_facialFifths: "Beşler dengesi",
  metric_symmetry: "Simetri",
  metric_redness: "Kızarıklık",
  metric_texture: "Pürüzsüzlük",
  metric_shine: "Parlaklık/yağ",
  metric_evenness: "Ton homojenliği",

  // ADIM 2 (2026-09-04): 3-slot ağız içi fotoğraf yükleme akışı.
  intraoral_consent_title: "Başlamadan önce",
  intraoral_consent_body:
    "Fotoğraflarınız güvenli şekilde yüklenecek ve yalnızca değerlendirme amacıyla kullanılacaktır. Tıbbi teşhis değildir. Canlı kamera modunda görüntü yalnızca cihazınızda işlenir; sunucuya yalnızca onayladığınız kare gönderilir.",
  intraoral_consent_checkbox: "Yukarıdaki bilgilendirmeyi okudum, fotoğraflarımın işlenmesine onay veriyorum.",
  intraoral_consent_continue: "Devam et",

  // ADIM 6a (2026-09-04): canlı kamera modu.
  live_start: "Kamerayla çek",
  live_cancel: "İptal",
  live_switch_to_file: "Dosyadan seç",
  live_switch_camera: "Kamerayı değiştir",
  live_captured: "Fotoğraf çekildi",
  live_hint_wrong_angle: "Kamerayı doğru açıya getirin",
  live_hint_low_confidence: "Açıyı netleştirin",
  live_hint_blurry: "Kamerayı sabit tutun, görüntü bulanık",
  live_hint_dark: "Daha aydınlık bir ortama geçin",
  live_hint_ready: "Harika, sabit tutun…",
  live_error_permission: "Kamera izni verilmedi",
  live_error_no_camera: "Kamera bulunamadı",
  live_error_model: "Canlı mod şu an kullanılamıyor",
  live_privacy_note: "Görüntü cihazınızda işlenir; yalnız onayladığınız kare gönderilir.",
  live_loading: "Yükleniyor…",
  slot_front_smile: "Gülümserken önden görünüm",
  slot_upper_occlusal: "Üst çene, ısırma yüzeyi",
  slot_lower_occlusal: "Alt çene, ısırma yüzeyi",
  hint_front_smile: "Gülümserken kameraya doğrudan bakın, tüm dişleriniz görünsün.",
  hint_upper_occlusal: "Ağzınızı açın, üst dişlerinizin ısırma yüzeyi kameraya baksın. Kamerayı ağzınıza iyice yaklaştırın.",
  hint_lower_occlusal: "Ağzınızı açın, alt dişlerinizin ısırma yüzeyi kameraya baksın. Kamerayı ağzınıza iyice yaklaştırın.",
  live_tip_flip_phone: "Telefonu baş aşağı çevirerek tutmak, kamerayı ağzınıza yaklaştırmayı kolaylaştırabilir.",
  live_tip_more_light: "Üst çene alt bölgeyi gölgeleyebilir — yeterli ışık yoksa oda ışığını artırın ya da yakınınızda ek bir ışık kaynağı tutturun.",
  slot_example_pending: "Örnek görsel yakında",
  slot_choose_photo: "Fotoğraf seç / çek",
  slot_retake: "Tekrar çek",
  slot_status_blurry: "Bulanık, tekrar çekin",
  slot_status_dark: "Işık yetersiz",
  slot_status_ok: "Fotoğraf uygun",
  slot_status_forced: "Bu fotoğrafla devam ediliyor — kalite düşük olabilir",
  slot_status_unreadable: "Bu dosya okunamadı, lütfen JPEG/PNG deneyin",
  slot_use_anyway: "Yine de bu fotoğrafı kullan",
  slots_progress: "Fotoğraf",
  slots_continue: "Devam et",
  slots_placeholder_next: "Fotoğraflarınız hazır. Yükleme adımı yakında.",
  uploading_title: "Fotoğraflar yükleniyor…",
  uploading_progress: "Yüklendi",
  uploading_failed: "Yükleme başarısız",
  uploading_retry: "Tekrar dene",
  uploading_error_all: "Bağlantı kurulamadı. İnternetinizi kontrol edip tekrar deneyin.",
  uploading_done: "Fotoğraflar yüklendi. Form adımı yakında.",
};

const EN: Dict = {
  app_title: "Smile Assessment",
  app_subtitle: "Runs on your device. Your images are never uploaded.",
  privacy_badge: "🔒 Your data is processed on your device, not sent to any server",
  consent_title: "Before you begin",
  consent_body:
    "Your face is analysed from 3 angles entirely on your device; images are never uploaded. Not a medical diagnosis.",
  consent_accept: "I understand, start",
  start_camera: "Open camera",
  step_label: "Step",
  capture_btn: "Capture",
  retake_btn: "Retake",
  next_btn: "Next",
  hint_frontal: "Face the camera directly, looking straight ahead.",
  hint_left_three_quarter: "Turn your head slightly to the left (~45°).",
  hint_right_three_quarter: "Turn your head slightly to the right (~45°).",
  warn_turn_left: "Turn slightly left",
  warn_turn_right: "Turn slightly right",
  warn_chin_down: "Lower your chin slightly",
  warn_chin_up: "Raise your chin slightly",
  warn_straighten: "Straighten your head",
  warn_blur: "Image is blurry, hold steady",
  warn_no_face: "No face detected, move into frame",
  ok_aligned: "Aligned ✓",
  loading_model: "Loading face analysis model…",
  model_error: "Could not load the analysis model. Check your internet connection.",
  done_body: "3/3 frames captured.",
  done_title: "Pre-assessment result",
  analysis_overall: "Overall",
  analysis_failed: "A sharp frontal frame is required for analysis. Please try again.",
  analysis_note: "Scores reflect closeness to aesthetic reference averages — not 'normal/abnormal'.",
  col_metric: "Metric",
  col_value: "Value",
  col_ref: "Reference",
  col_score: "Score",
  section_geometry: "Face ratio / geometry",
  section_skin: "Skin surface (heuristic)",
  download_pdf: "Download PDF report",
  lead_title: "Want an expert opinion?",
  lead_sub: "Leave your details and the Nexwell team will reach you on WhatsApp. Your images are never sent; only the score summary is shared.",
  lead_name: "Full name",
  lead_phone: "Phone (WhatsApp)",
  lead_email: "Email",
  lead_consent: "I give explicit consent for my personal data (name, phone, email, score summary) to be processed by Nexwell for contact purposes.",
  lead_submit: "Send",
  lead_sending: "Sending…",
  lead_ok: "Thank you! We will reach out shortly.",
  lead_consent_required: "Consent is required to send.",
  lead_no_webhook: "The form is not configured yet (no webhook set).",
  lead_error: "Could not send, please try again.",
  lead_country: "Country",
  gate_ready: "Your pre-assessment is ready",
  gate_title: "Enter your details to see your result",
  gate_sub: "Leave your contact details to view your report. Nexwell experts will reach you on WhatsApp. Your images are never sent; only the score summary is shared.",
  gate_submit: "Show my result",
  gate_fill_required: "Please fill in all fields correctly.",
  teaser_title: "Here is your pre-assessment score",
  teaser_sub: "Your facial proportions and skin surface were analysed on your device. Here is your overall result:",
  teaser_overall_cap: "Overall harmony score",
  teaser_highlight_intro: "Your standout measurements",
  teaser_cta: "Unlock your full report",
  teaser_locked_hint: "The full metric breakdown, visual report, PDF download and personal improvement tips are one step away.",
  report_tips_title: "How to improve",
  report_tips_intro: "The tips below are general guidance; for a personalised plan, consult a Nexwell specialist.",
  legal_disclaimer:
    "This assessment is for information only and is not a medical diagnosis or treatment recommendation. Consult a physician for a definitive evaluation.",
  camera_error: "Could not access the camera. Please grant camera permission and use an HTTPS connection.",
  camera_error_demo_cta: "Continue without a camera — view a sample report",
  optional_suffix: " (optional)",
  turnstile_verifying: "Verifying…",
  turnstile_error: "Verification failed, please try again.",
  metric_faceLengthToWidth: "Face length/width",
  metric_mouthToNoseWidth: "Mouth/nose width",
  metric_upperToLowerLip: "Upper/lower lip",
  metric_intercanthalRatio: "Eye spacing balance",
  metric_facialThirds: "Facial thirds",
  metric_facialFifths: "Facial fifths",
  metric_symmetry: "Symmetry",
  metric_redness: "Redness",
  metric_texture: "Smoothness",
  metric_shine: "Shine/oil",
  metric_evenness: "Tone evenness",

  // STEP 2 (2026-09-04): 3-slot intraoral photo upload flow.
  intraoral_consent_title: "Before you begin",
  intraoral_consent_body:
    "Your photos will be uploaded securely and used only for assessment purposes. Not a medical diagnosis. In live camera mode, your image is processed only on your device; only the frame you approve is sent to the server.",
  intraoral_consent_checkbox: "I have read the notice above and consent to my photos being processed.",
  intraoral_consent_continue: "Continue",

  // STEP 6a (2026-09-04): live camera mode.
  live_start: "Use camera",
  live_cancel: "Cancel",
  live_switch_to_file: "Choose from file",
  live_switch_camera: "Switch camera",
  live_captured: "Photo captured",
  live_hint_wrong_angle: "Adjust the camera to the right angle",
  live_hint_low_confidence: "Bring the angle into clearer focus",
  live_hint_blurry: "Hold the camera steady, image is blurry",
  live_hint_dark: "Move to a brighter area",
  live_hint_ready: "Great, hold steady…",
  live_error_permission: "Camera permission was denied",
  live_error_no_camera: "No camera found",
  live_error_model: "Live mode is currently unavailable",
  live_privacy_note: "Your image is processed on your device; only the frame you approve is sent.",
  live_loading: "Loading…",
  slot_front_smile: "Front view while smiling",
  slot_upper_occlusal: "Upper jaw, occlusal surface",
  slot_lower_occlusal: "Lower jaw, occlusal surface",
  hint_front_smile: "Look straight at the camera while smiling, all teeth visible.",
  hint_upper_occlusal: "Open your mouth, aim the biting surface of your upper teeth at the camera. Bring the camera close to your mouth.",
  hint_lower_occlusal: "Open your mouth, aim the biting surface of your lower teeth at the camera. Bring the camera close to your mouth.",
  live_tip_flip_phone: "Holding your phone upside down can make it easier to bring the camera close to your mouth.",
  live_tip_more_light: "Your upper jaw can shadow the lower area — if there isn't enough light, brighten the room or have someone hold an extra light source nearby.",
  slot_example_pending: "Example photo coming soon",
  slot_choose_photo: "Choose / take photo",
  slot_retake: "Retake",
  slot_status_blurry: "Blurry, please retake",
  slot_status_dark: "Not enough light",
  slot_status_ok: "Photo looks good",
  slot_status_forced: "Continuing with this photo — quality may be low",
  slot_status_unreadable: "This file could not be read, try JPEG/PNG",
  slot_use_anyway: "Use this photo anyway",
  slots_progress: "Photo",
  slots_continue: "Continue",
  slots_placeholder_next: "Your photos are ready. Upload step is coming soon.",
  uploading_title: "Uploading photos…",
  uploading_progress: "Uploaded",
  uploading_failed: "Upload failed",
  uploading_retry: "Retry",
  uploading_error_all: "Could not connect. Please check your connection and try again.",
  uploading_done: "Photos uploaded. Form step is coming soon.",
};

const FR: Dict = {
  app_title: "Évaluation du sourire",
  app_subtitle: "Fonctionne sur votre appareil. Vos images ne sont jamais envoyées.",
  privacy_badge: "🔒 Vos données sont traitées sur votre appareil, envoyées à aucun serveur",
  consent_title: "Avant de commencer",
  consent_body:
    "Votre visage est analysé sous 3 angles, entièrement sur votre appareil ; aucune image n'est envoyée. Pas un diagnostic médical.",
  consent_accept: "J'ai compris, commencer",
  start_camera: "Ouvrir la caméra",
  step_label: "Étape",
  capture_btn: "Capturer",
  retake_btn: "Reprendre",
  next_btn: "Suivant",
  hint_frontal: "Placez votre visage face à la caméra, regard droit devant.",
  hint_left_three_quarter: "Tournez légèrement la tête à gauche (~45°).",
  hint_right_three_quarter: "Tournez légèrement la tête à droite (~45°).",
  warn_turn_left: "Tournez un peu à gauche",
  warn_turn_right: "Tournez un peu à droite",
  warn_chin_down: "Baissez légèrement le menton",
  warn_chin_up: "Relevez légèrement le menton",
  warn_straighten: "Redressez la tête",
  warn_blur: "Image floue, tenez stable",
  warn_no_face: "Aucun visage détecté, placez-vous dans le cadre",
  ok_aligned: "Aligné ✓",
  loading_model: "Chargement du modèle d'analyse…",
  model_error: "Impossible de charger le modèle. Vérifiez votre connexion internet.",
  done_body: "3/3 images capturées.",
  done_title: "Résultat de la pré-évaluation",
  analysis_overall: "Harmonie globale",
  analysis_failed: "Une image frontale nette est requise. Veuillez réessayer.",
  analysis_note: "Les scores reflètent la proximité avec des moyennes de référence esthétiques — pas « normal/anormal ».",
  col_metric: "Mesure",
  col_value: "Valeur",
  col_ref: "Référence",
  col_score: "Score",
  section_geometry: "Proportions / géométrie du visage",
  section_skin: "Surface cutanée (heuristique)",
  download_pdf: "Télécharger le PDF",
  lead_title: "Vous voulez l'avis d'un expert ?",
  lead_sub: "Laissez vos coordonnées et l'équipe Nexwell vous contactera sur WhatsApp. Vos images ne sont pas envoyées ; seul le résumé des scores est partagé.",
  lead_name: "Nom complet",
  lead_phone: "Téléphone (WhatsApp)",
  lead_email: "E-mail",
  lead_consent: "Je consens expressément au traitement de mes données personnelles (nom, téléphone, e-mail, résumé des scores) par Nexwell à des fins de contact.",
  lead_submit: "Envoyer",
  lead_sending: "Envoi…",
  lead_ok: "Merci ! Nous vous contacterons rapidement.",
  lead_consent_required: "Le consentement est requis pour envoyer.",
  lead_no_webhook: "Le formulaire n'est pas encore configuré (aucun webhook).",
  lead_error: "Échec de l'envoi, veuillez réessayer.",
  lead_country: "Pays",
  gate_ready: "Votre pré-évaluation est prête",
  gate_title: "Saisissez vos coordonnées pour voir votre résultat",
  gate_sub: "Laissez vos coordonnées pour consulter votre rapport. Les experts Nexwell vous contacteront sur WhatsApp. Vos images ne sont pas envoyées ; seul le résumé des scores est partagé.",
  gate_submit: "Voir mon résultat",
  gate_fill_required: "Veuillez remplir tous les champs correctement.",
  teaser_title: "Voici votre score de pré-évaluation",
  teaser_sub: "Vos proportions faciales et votre surface cutanée ont été analysées sur votre appareil. Voici votre résultat global :",
  teaser_overall_cap: "Score d'harmonie globale",
  teaser_highlight_intro: "Vos mesures remarquables",
  teaser_cta: "Débloquez votre rapport complet",
  teaser_locked_hint: "Le détail complet des mesures, le rapport visuel, le téléchargement PDF et des conseils personnalisés sont à une étape.",
  report_tips_title: "Comment s'améliorer",
  report_tips_intro: "Les conseils ci-dessous sont d'ordre général ; pour un plan personnalisé, consultez un spécialiste Nexwell.",
  legal_disclaimer:
    "Cette évaluation est purement informative et ne constitue pas un diagnostic ou une recommandation de traitement. Consultez un médecin pour une évaluation définitive.",
  camera_error: "Accès à la caméra impossible. Autorisez la caméra et utilisez une connexion HTTPS.",
  camera_error_demo_cta: "Continuer sans caméra — voir un exemple de rapport",
  optional_suffix: " (facultatif)",
  turnstile_verifying: "Vérification…",
  turnstile_error: "Échec de la vérification, veuillez réessayer.",
  metric_faceLengthToWidth: "Longueur/largeur du visage",
  metric_mouthToNoseWidth: "Largeur bouche/nez",
  metric_upperToLowerLip: "Lèvre sup./inf.",
  metric_intercanthalRatio: "Équilibre écart des yeux",
  metric_facialThirds: "Tiers du visage",
  metric_facialFifths: "Cinquièmes du visage",
  metric_symmetry: "Symétrie",
  metric_redness: "Rougeur",
  metric_texture: "Lissé",
  metric_shine: "Brillance/sébum",
  metric_evenness: "Homogénéité du teint",

  // STEP 2 (2026-09-04): flux de photos intra-orales, 3 emplacements.
  intraoral_consent_title: "Avant de commencer",
  intraoral_consent_body:
    "Vos photos seront téléversées en toute sécurité et utilisées uniquement à des fins d'évaluation. Ce n'est pas un diagnostic médical. En mode caméra en direct, votre image est traitée uniquement sur votre appareil ; seule l'image que vous validez est envoyée au serveur.",
  intraoral_consent_checkbox: "J'ai lu les informations ci-dessus et je consens au traitement de mes photos.",
  intraoral_consent_continue: "Continuer",

  // STEP 6a (2026-09-04): mode caméra en direct.
  live_start: "Utiliser la caméra",
  live_cancel: "Annuler",
  live_switch_to_file: "Choisir un fichier",
  live_switch_camera: "Changer de caméra",
  live_captured: "Photo capturée",
  live_hint_wrong_angle: "Ajustez la caméra au bon angle",
  live_hint_low_confidence: "Affinez l'angle",
  live_hint_blurry: "Tenez la caméra stable, l'image est floue",
  live_hint_dark: "Déplacez-vous vers un endroit plus lumineux",
  live_hint_ready: "Parfait, restez immobile…",
  live_error_permission: "L'autorisation d'accès à la caméra a été refusée",
  live_error_no_camera: "Aucune caméra détectée",
  live_error_model: "Le mode en direct est actuellement indisponible",
  live_privacy_note: "Votre image est traitée sur votre appareil ; seule l'image que vous validez est envoyée.",
  live_loading: "Chargement…",
  slot_front_smile: "Vue de face en souriant",
  slot_upper_occlusal: "Mâchoire supérieure, face occlusale",
  slot_lower_occlusal: "Mâchoire inférieure, face occlusale",
  hint_front_smile: "Regardez la caméra en souriant, toutes vos dents visibles.",
  hint_upper_occlusal: "Ouvrez la bouche, dirigez la surface masticatoire de vos dents supérieures vers la caméra. Rapprochez bien la caméra de votre bouche.",
  hint_lower_occlusal: "Ouvrez la bouche, dirigez la surface masticatoire de vos dents inférieures vers la caméra. Rapprochez bien la caméra de votre bouche.",
  live_tip_flip_phone: "Tenir votre téléphone à l'envers peut faciliter le rapprochement de la caméra de votre bouche.",
  live_tip_more_light: "Votre mâchoire supérieure peut faire de l'ombre sur la zone inférieure — si la lumière est insuffisante, éclairez davantage la pièce ou faites tenir une source de lumière supplémentaire à proximité.",
  slot_example_pending: "Photo d'exemple bientôt disponible",
  slot_choose_photo: "Choisir / prendre une photo",
  slot_retake: "Reprendre",
  slot_status_blurry: "Floue, veuillez reprendre",
  slot_status_dark: "Éclairage insuffisant",
  slot_status_ok: "Photo correcte",
  slot_status_forced: "Cette photo est utilisée quand même — la qualité peut être faible",
  slot_status_unreadable: "Ce fichier n'a pas pu être lu, essayez JPEG/PNG",
  slot_use_anyway: "Utiliser quand même cette photo",
  slots_progress: "Photo",
  slots_continue: "Continuer",
  slots_placeholder_next: "Vos photos sont prêtes. L'étape de téléversement arrive bientôt.",
  uploading_title: "Téléversement des photos…",
  uploading_progress: "Téléversée",
  uploading_failed: "Échec du téléversement",
  uploading_retry: "Réessayer",
  uploading_error_all: "Connexion impossible. Vérifiez votre connexion et réessayez.",
  uploading_done: "Photos téléversées. L'étape du formulaire arrive bientôt.",
};

const DE: Dict = {
  app_title: "Lächeln-Bewertung",
  app_subtitle: "Läuft auf Ihrem Gerät. Ihre Bilder werden nie hochgeladen.",
  privacy_badge: "🔒 Ihre Daten werden auf Ihrem Gerät verarbeitet, an keinen Server gesendet",
  consent_title: "Bevor Sie beginnen",
  consent_body:
    "Ihr Gesicht wird aus 3 Winkeln vollständig auf Ihrem Gerät analysiert; keine Bilder werden hochgeladen. Keine medizinische Diagnose.",
  consent_accept: "Verstanden, starten",
  start_camera: "Kamera öffnen",
  step_label: "Schritt",
  capture_btn: "Aufnehmen",
  retake_btn: "Wiederholen",
  next_btn: "Weiter",
  hint_frontal: "Schauen Sie gerade in die Kamera, Gesicht frontal.",
  hint_left_three_quarter: "Drehen Sie den Kopf leicht nach links (~45°).",
  hint_right_three_quarter: "Drehen Sie den Kopf leicht nach rechts (~45°).",
  warn_turn_left: "Etwas nach links drehen",
  warn_turn_right: "Etwas nach rechts drehen",
  warn_chin_down: "Kinn leicht senken",
  warn_chin_up: "Kinn leicht heben",
  warn_straighten: "Kopf gerade halten",
  warn_blur: "Bild unscharf, ruhig halten",
  warn_no_face: "Kein Gesicht erkannt, ins Bild rücken",
  ok_aligned: "Ausgerichtet ✓",
  loading_model: "Analyse-Modell wird geladen…",
  model_error: "Modell konnte nicht geladen werden. Prüfen Sie Ihre Internetverbindung.",
  done_body: "3/3 Aufnahmen erfasst.",
  done_title: "Ergebnis der Vorab-Analyse",
  analysis_overall: "Gesamtharmonie",
  analysis_failed: "Ein scharfes Frontalbild ist erforderlich. Bitte erneut versuchen.",
  analysis_note: "Werte zeigen die Nähe zu ästhetischen Durchschnittswerten — nicht „normal/anormal“.",
  col_metric: "Messung",
  col_value: "Wert",
  col_ref: "Referenz",
  col_score: "Score",
  section_geometry: "Gesichtsproportionen / Geometrie",
  section_skin: "Hautoberfläche (heuristisch)",
  download_pdf: "PDF-Bericht laden",
  lead_title: "Möchten Sie eine Experteneinschätzung?",
  lead_sub: "Hinterlassen Sie Ihre Daten, das Nexwell-Team meldet sich per WhatsApp. Ihre Bilder werden nicht gesendet; nur die Score-Zusammenfassung wird geteilt.",
  lead_name: "Vollständiger Name",
  lead_phone: "Telefon (WhatsApp)",
  lead_email: "E-Mail",
  lead_consent: "Ich willige ausdrücklich ein, dass meine personenbezogenen Daten (Name, Telefon, E-Mail, Score-Zusammenfassung) von Nexwell zu Kontaktzwecken verarbeitet werden.",
  lead_submit: "Senden",
  lead_sending: "Senden…",
  lead_ok: "Danke! Wir melden uns in Kürze.",
  lead_consent_required: "Zum Senden ist die Einwilligung erforderlich.",
  lead_no_webhook: "Das Formular ist noch nicht konfiguriert (kein Webhook).",
  lead_error: "Senden fehlgeschlagen, bitte erneut versuchen.",
  lead_country: "Land",
  gate_ready: "Ihre Vorab-Analyse ist fertig",
  gate_title: "Geben Sie Ihre Daten ein, um Ihr Ergebnis zu sehen",
  gate_sub: "Hinterlassen Sie Ihre Kontaktdaten, um Ihren Bericht zu sehen. Nexwell-Experten melden sich per WhatsApp. Ihre Bilder werden nicht gesendet; nur die Score-Zusammenfassung wird geteilt.",
  gate_submit: "Mein Ergebnis anzeigen",
  gate_fill_required: "Bitte füllen Sie alle Felder korrekt aus.",
  teaser_title: "Hier ist Ihr Vorab-Score",
  teaser_sub: "Ihre Gesichtsproportionen und Hautoberfläche wurden auf Ihrem Gerät analysiert. Hier ist Ihr Gesamtergebnis:",
  teaser_overall_cap: "Gesamtharmonie-Score",
  teaser_highlight_intro: "Ihre herausragenden Messwerte",
  teaser_cta: "Vollständigen Bericht freischalten",
  teaser_locked_hint: "Die vollständige Messwert-Aufschlüsselung, der visuelle Bericht, der PDF-Download und persönliche Verbesserungstipps sind nur einen Schritt entfernt.",
  report_tips_title: "So verbessern Sie sich",
  report_tips_intro: "Die folgenden Tipps sind allgemeine Hinweise; für einen persönlichen Plan wenden Sie sich an einen Nexwell-Spezialisten.",
  legal_disclaimer:
    "Diese Einschätzung dient nur der Information und ist keine medizinische Diagnose oder Behandlungsempfehlung. Für eine endgültige Beurteilung konsultieren Sie einen Arzt.",
  camera_error: "Kamerazugriff nicht möglich. Bitte Kamera erlauben und HTTPS-Verbindung nutzen.",
  camera_error_demo_cta: "Ohne Kamera fortfahren — Beispielbericht ansehen",
  optional_suffix: " (optional)",
  turnstile_verifying: "Wird überprüft…",
  turnstile_error: "Überprüfung fehlgeschlagen, bitte erneut versuchen.",
  metric_faceLengthToWidth: "Gesichtslänge/-breite",
  metric_mouthToNoseWidth: "Mund-/Nasenbreite",
  metric_upperToLowerLip: "Ober-/Unterlippe",
  metric_intercanthalRatio: "Augenabstand-Balance",
  metric_facialThirds: "Gesichtsdrittel",
  metric_facialFifths: "Gesichtsfünftel",
  metric_symmetry: "Symmetrie",
  metric_redness: "Rötung",
  metric_texture: "Glätte",
  metric_shine: "Glanz/Öl",
  metric_evenness: "Hautton-Gleichmäßigkeit",

  // STEP 2 (2026-09-04): 3-Slot-Upload-Flow für Mundinnenfotos.
  intraoral_consent_title: "Bevor Sie beginnen",
  intraoral_consent_body:
    "Ihre Fotos werden sicher hochgeladen und nur zu Bewertungszwecken verwendet. Keine medizinische Diagnose. Im Live-Kameramodus wird Ihr Bild nur auf Ihrem Gerät verarbeitet; nur das von Ihnen bestätigte Bild wird an den Server gesendet.",
  intraoral_consent_checkbox: "Ich habe den obigen Hinweis gelesen und stimme der Verarbeitung meiner Fotos zu.",
  intraoral_consent_continue: "Weiter",

  // STEP 6a (2026-09-04): Live-Kameramodus.
  live_start: "Kamera verwenden",
  live_cancel: "Abbrechen",
  live_switch_to_file: "Datei auswählen",
  live_switch_camera: "Kamera wechseln",
  live_captured: "Foto aufgenommen",
  live_hint_wrong_angle: "Bringen Sie die Kamera in den richtigen Winkel",
  live_hint_low_confidence: "Richten Sie den Winkel genauer aus",
  live_hint_blurry: "Halten Sie die Kamera ruhig, das Bild ist unscharf",
  live_hint_dark: "Wechseln Sie zu einer helleren Umgebung",
  live_hint_ready: "Großartig, halten Sie ruhig…",
  live_error_permission: "Kamerazugriff wurde verweigert",
  live_error_no_camera: "Keine Kamera gefunden",
  live_error_model: "Der Live-Modus ist derzeit nicht verfügbar",
  live_privacy_note: "Ihr Bild wird auf Ihrem Gerät verarbeitet; nur das von Ihnen bestätigte Bild wird gesendet.",
  live_loading: "Wird geladen…",
  slot_front_smile: "Frontalansicht beim Lächeln",
  slot_upper_occlusal: "Oberkiefer, Kaufläche",
  slot_lower_occlusal: "Unterkiefer, Kaufläche",
  hint_front_smile: "Schauen Sie beim Lächeln direkt in die Kamera, alle Zähne sichtbar.",
  hint_upper_occlusal: "Öffnen Sie den Mund, richten Sie die Kaufläche Ihrer oberen Zähne auf die Kamera. Halten Sie die Kamera nah an Ihren Mund.",
  hint_lower_occlusal: "Öffnen Sie den Mund, richten Sie die Kaufläche Ihrer unteren Zähne auf die Kamera. Halten Sie die Kamera nah an Ihren Mund.",
  live_tip_flip_phone: "Das Telefon verkehrt herum zu halten kann es erleichtern, die Kamera nah an Ihren Mund zu bringen.",
  live_tip_more_light: "Ihr Oberkiefer kann den unteren Bereich verschatten — wenn nicht genug Licht vorhanden ist, erhellen Sie den Raum oder lassen Sie eine zusätzliche Lichtquelle in der Nähe halten.",
  slot_example_pending: "Beispielfoto folgt in Kürze",
  slot_choose_photo: "Foto auswählen / aufnehmen",
  slot_retake: "Wiederholen",
  slot_status_blurry: "Unscharf, bitte erneut aufnehmen",
  slot_status_dark: "Nicht genug Licht",
  slot_status_ok: "Foto sieht gut aus",
  slot_status_forced: "Es wird mit diesem Foto fortgefahren — die Qualität kann gering sein",
  slot_status_unreadable: "Diese Datei konnte nicht gelesen werden, bitte JPEG/PNG versuchen",
  slot_use_anyway: "Dieses Foto trotzdem verwenden",
  slots_progress: "Foto",
  slots_continue: "Weiter",
  slots_placeholder_next: "Ihre Fotos sind bereit. Der Upload-Schritt folgt in Kürze.",
  uploading_title: "Fotos werden hochgeladen…",
  uploading_progress: "Hochgeladen",
  uploading_failed: "Hochladen fehlgeschlagen",
  uploading_retry: "Erneut versuchen",
  uploading_error_all: "Verbindung fehlgeschlagen. Bitte Internetverbindung prüfen und erneut versuchen.",
  uploading_done: "Fotos hochgeladen. Der Formular-Schritt folgt in Kürze.",
};

// NOT: RU çevirisi taslak — native gözden geçirme önerilir.
const RU: Dict = {
  app_title: "Оценка улыбки",
  app_subtitle: "Работает на вашем устройстве. Изображения никуда не загружаются.",
  privacy_badge: "🔒 Данные обрабатываются на вашем устройстве и не отправляются на сервер",
  consent_title: "Перед началом",
  consent_body:
    "Ваше лицо анализируется с 3 ракурсов полностью на вашем устройстве; изображения не загружаются. Не медицинский диагноз.",
  consent_accept: "Понятно, начать",
  start_camera: "Открыть камеру",
  step_label: "Шаг",
  capture_btn: "Снять",
  retake_btn: "Переснять",
  next_btn: "Далее",
  hint_frontal: "Смотрите прямо в камеру, лицо анфас.",
  hint_left_three_quarter: "Слегка поверните голову влево (~45°).",
  hint_right_three_quarter: "Слегка поверните голову вправо (~45°).",
  warn_turn_left: "Поверните немного влево",
  warn_turn_right: "Поверните немного вправо",
  warn_chin_down: "Слегка опустите подбородок",
  warn_chin_up: "Слегка поднимите подбородок",
  warn_straighten: "Выпрямите голову",
  warn_blur: "Изображение размыто, держите ровно",
  warn_no_face: "Лицо не обнаружено, войдите в кадр",
  ok_aligned: "Выровнено ✓",
  loading_model: "Загрузка модели анализа лица…",
  model_error: "Не удалось загрузить модель. Проверьте подключение к интернету.",
  done_body: "Сделано 3/3 кадра.",
  done_title: "Результат предварительной оценки",
  analysis_overall: "Общая гармония",
  analysis_failed: "Нужен чёткий кадр анфас. Пожалуйста, попробуйте снова.",
  analysis_note: "Баллы отражают близость к эстетическим средним значениям — не «норма/патология».",
  col_metric: "Параметр",
  col_value: "Значение",
  col_ref: "Норма",
  col_score: "Балл",
  section_geometry: "Пропорции / геометрия лица",
  section_skin: "Поверхность кожи (эвристика)",
  download_pdf: "Скачать PDF-отчёт",
  lead_title: "Хотите консультацию эксперта?",
  lead_sub: "Оставьте контакты, и команда Nexwell свяжется с вами в WhatsApp. Изображения не отправляются; передаётся только сводка баллов.",
  lead_name: "Имя и фамилия",
  lead_phone: "Телефон (WhatsApp)",
  lead_email: "E-mail",
  lead_consent: "Я даю явное согласие на обработку моих персональных данных (имя, телефон, e-mail, сводка баллов) компанией Nexwell в целях связи.",
  lead_submit: "Отправить",
  lead_sending: "Отправка…",
  lead_ok: "Спасибо! Мы скоро с вами свяжемся.",
  lead_consent_required: "Для отправки требуется согласие.",
  lead_no_webhook: "Форма пока не настроена (нет webhook).",
  lead_error: "Не удалось отправить, попробуйте снова.",
  lead_country: "Страна",
  gate_ready: "Ваша предварительная оценка готова",
  gate_title: "Введите данные, чтобы увидеть результат",
  gate_sub: "Оставьте контактные данные, чтобы увидеть отчёт. Эксперты Nexwell свяжутся с вами в WhatsApp. Изображения не отправляются; передаётся только сводка баллов.",
  gate_submit: "Показать результат",
  gate_fill_required: "Пожалуйста, заполните все поля корректно.",
  teaser_title: "Вот ваш балл предварительной оценки",
  teaser_sub: "Пропорции лица и поверхность кожи проанализированы на вашем устройстве. Вот ваш общий результат:",
  teaser_overall_cap: "Балл общей гармонии",
  teaser_highlight_intro: "Ваши заметные показатели",
  teaser_cta: "Открыть полный отчёт",
  teaser_locked_hint: "Полная разбивка показателей, визуальный отчёт, PDF и персональные советы — в одном шаге.",
  report_tips_title: "Как улучшить",
  report_tips_intro: "Приведённые советы носят общий характер; для индивидуального плана обратитесь к специалисту Nexwell.",
  legal_disclaimer:
    "Эта оценка носит исключительно информационный характер и не является диагнозом или рекомендацией по лечению. Для точной оценки обратитесь к врачу.",
  camera_error: "Нет доступа к камере. Разрешите камеру и используйте HTTPS-соединение.",
  camera_error_demo_cta: "Продолжить без камеры — посмотреть пример отчёта",
  optional_suffix: " (необязательно)",
  turnstile_verifying: "Проверка…",
  turnstile_error: "Проверка не удалась, попробуйте снова.",
  metric_faceLengthToWidth: "Длина/ширина лица",
  metric_mouthToNoseWidth: "Ширина рта/носа",
  metric_upperToLowerLip: "Верхняя/нижняя губа",
  metric_intercanthalRatio: "Баланс расстояния глаз",
  metric_facialThirds: "Трети лица",
  metric_facialFifths: "Пятые доли лица",
  metric_symmetry: "Симметрия",
  metric_redness: "Покраснение",
  metric_texture: "Гладкость",
  metric_shine: "Блеск/жирность",
  metric_evenness: "Ровность тона",

  // ШАГ 2 (2026-09-04): загрузка 3 внутриротовых фото по слотам.
  intraoral_consent_title: "Перед началом",
  intraoral_consent_body:
    "Ваши фотографии будут безопасно загружены и использованы только для оценки. Это не медицинский диагноз. В режиме камеры реального времени изображение обрабатывается только на вашем устройстве; на сервер отправляется только подтверждённый вами кадр.",
  intraoral_consent_checkbox: "Я прочитал(а) уведомление выше и даю согласие на обработку моих фотографий.",
  intraoral_consent_continue: "Продолжить",

  // ШАГ 6a (2026-09-04): режим камеры реального времени.
  live_start: "Использовать камеру",
  live_cancel: "Отмена",
  live_switch_to_file: "Выбрать файл",
  live_switch_camera: "Сменить камеру",
  live_captured: "Фото сделано",
  live_hint_wrong_angle: "Установите камеру под правильным углом",
  live_hint_low_confidence: "Скорректируйте угол",
  live_hint_blurry: "Держите камеру неподвижно, изображение размыто",
  live_hint_dark: "Перейдите в более освещённое место",
  live_hint_ready: "Отлично, держите неподвижно…",
  live_error_permission: "Доступ к камере не предоставлен",
  live_error_no_camera: "Камера не найдена",
  live_error_model: "Режим реального времени сейчас недоступен",
  live_privacy_note: "Изображение обрабатывается на вашем устройстве; отправляется только подтверждённый вами кадр.",
  live_loading: "Загрузка…",
  slot_front_smile: "Вид спереди с улыбкой",
  slot_upper_occlusal: "Верхняя челюсть, жевательная поверхность",
  slot_lower_occlusal: "Нижняя челюсть, жевательная поверхность",
  hint_front_smile: "Смотрите прямо в камеру с улыбкой, чтобы были видны все зубы.",
  hint_upper_occlusal: "Откройте рот, направьте жевательную поверхность верхних зубов на камеру. Поднесите камеру близко ко рту.",
  hint_lower_occlusal: "Откройте рот, направьте жевательную поверхность нижних зубов на камеру. Поднесите камеру близко ко рту.",
  live_tip_flip_phone: "Если держать телефон перевёрнутым, будет легче поднести камеру близко ко рту.",
  live_tip_more_light: "Верхняя челюсть может затенять нижнюю область — если света недостаточно, сделайте комнату светлее или попросите кого-нибудь посветить рядом дополнительным источником света.",
  slot_example_pending: "Пример фото скоро появится",
  slot_choose_photo: "Выбрать / сделать фото",
  slot_retake: "Переснять",
  slot_status_blurry: "Размыто, переснимите",
  slot_status_dark: "Недостаточно света",
  slot_status_ok: "Фото подходит",
  slot_status_forced: "Продолжаем с этим фото — качество может быть низким",
  slot_status_unreadable: "Не удалось прочитать файл, попробуйте JPEG/PNG",
  slot_use_anyway: "Всё равно использовать это фото",
  slots_progress: "Фото",
  slots_continue: "Продолжить",
  slots_placeholder_next: "Ваши фотографии готовы. Шаг загрузки скоро появится.",
  uploading_title: "Загрузка фотографий…",
  uploading_progress: "Загружено",
  uploading_failed: "Загрузка не удалась",
  uploading_retry: "Повторить",
  uploading_error_all: "Не удалось подключиться. Проверьте подключение к интернету и попробуйте снова.",
  uploading_done: "Фотографии загружены. Шаг с формой скоро появится.",
};

// NOT: AR çevirisi taslak — native gözden geçirme önerilir. RTL.
const AR: Dict = {
  app_title: "تقييم الابتسامة",
  app_subtitle: "يعمل على جهازك. لا يتم رفع صورك إطلاقًا.",
  privacy_badge: "🔒 تتم معالجة بياناتك على جهازك ولا تُرسل إلى أي خادم",
  consent_title: "قبل أن تبدأ",
  consent_body:
    "يُحلَّل وجهك من 3 زوايا بالكامل على جهازك؛ ولا تُرفع أي صور. ليس تشخيصًا طبيًا.",
  consent_accept: "فهمت، ابدأ",
  start_camera: "افتح الكاميرا",
  step_label: "خطوة",
  capture_btn: "التقاط",
  retake_btn: "إعادة الالتقاط",
  next_btn: "التالي",
  hint_frontal: "واجه الكاميرا مباشرةً والنظر إلى الأمام.",
  hint_left_three_quarter: "أدر رأسك قليلًا إلى اليسار (~45°).",
  hint_right_three_quarter: "أدر رأسك قليلًا إلى اليمين (~45°).",
  warn_turn_left: "استدر قليلًا إلى اليسار",
  warn_turn_right: "استدر قليلًا إلى اليمين",
  warn_chin_down: "اخفض ذقنك قليلًا",
  warn_chin_up: "ارفع ذقنك قليلًا",
  warn_straighten: "اعدل رأسك",
  warn_blur: "الصورة غير واضحة، ثبّت الكاميرا",
  warn_no_face: "لم يتم اكتشاف وجه، ادخل ضمن الإطار",
  ok_aligned: "تمت المحاذاة ✓",
  loading_model: "جارٍ تحميل نموذج تحليل الوجه…",
  model_error: "تعذّر تحميل النموذج. تحقق من اتصالك بالإنترنت.",
  done_body: "تم التقاط 3/3 صور.",
  done_title: "نتيجة التقييم الأولي",
  analysis_overall: "الانسجام العام",
  analysis_failed: "يلزم وجود صورة أمامية واضحة. يرجى المحاولة مرة أخرى.",
  analysis_note: "تعكس الدرجات القرب من المتوسطات الجمالية المرجعية — وليست «طبيعي/غير طبيعي».",
  col_metric: "القياس",
  col_value: "القيمة",
  col_ref: "المرجع",
  col_score: "الدرجة",
  section_geometry: "نِسَب / هندسة الوجه",
  section_skin: "سطح البشرة (تقديري)",
  download_pdf: "تنزيل تقرير PDF",
  lead_title: "هل تريد رأي خبير؟",
  lead_sub: "اترك بياناتك وسيتواصل معك فريق Nexwell عبر واتساب. لا تُرسل صورك؛ تتم مشاركة ملخص الدرجات فقط.",
  lead_name: "الاسم الكامل",
  lead_phone: "الهاتف (واتساب)",
  lead_email: "البريد الإلكتروني",
  lead_consent: "أوافق صراحةً على معالجة بياناتي الشخصية (الاسم، الهاتف، البريد، ملخص الدرجات) من قِبل Nexwell لأغراض التواصل.",
  lead_submit: "إرسال",
  lead_sending: "جارٍ الإرسال…",
  lead_ok: "شكرًا! سنتواصل معك قريبًا.",
  lead_consent_required: "الموافقة مطلوبة للإرسال.",
  lead_no_webhook: "النموذج غير مُهيّأ بعد (لا يوجد webhook).",
  lead_error: "تعذّر الإرسال، يرجى المحاولة مرة أخرى.",
  lead_country: "الدولة",
  gate_ready: "تقييمك الأولي جاهز",
  gate_title: "أدخل بياناتك لرؤية نتيجتك",
  gate_sub: "اترك بيانات التواصل لعرض تقريرك. سيتواصل معك خبراء Nexwell عبر واتساب. لا تُرسل صورك؛ تتم مشاركة ملخص الدرجات فقط.",
  gate_submit: "عرض نتيجتي",
  gate_fill_required: "يرجى ملء جميع الحقول بشكل صحيح.",
  teaser_title: "إليك درجة تقييمك الأولي",
  teaser_sub: "تم تحليل نِسَب وجهك وسطح بشرتك على جهازك. إليك نتيجتك العامة:",
  teaser_overall_cap: "درجة الانسجام العام",
  teaser_highlight_intro: "أبرز قياساتك",
  teaser_cta: "افتح تقريرك الكامل",
  teaser_locked_hint: "التفصيل الكامل للقياسات، والتقرير المرئي، وتنزيل PDF، ونصائح التحسين الشخصية على بُعد خطوة واحدة.",
  report_tips_title: "كيفية التحسين",
  report_tips_intro: "النصائح أدناه إرشادات عامة؛ للحصول على خطة مخصصة، استشر أخصائي Nexwell.",
  legal_disclaimer:
    "هذا التقييم لأغراض المعلومات فقط وليس تشخيصًا طبيًا أو توصية علاجية. لتقييم دقيق، يُرجى استشارة طبيب.",
  camera_error: "تعذّر الوصول إلى الكاميرا. يرجى السماح بالكاميرا واستخدام اتصال HTTPS.",
  camera_error_demo_cta: "المتابعة بدون كاميرا — عرض تقرير تجريبي",
  optional_suffix: " (اختياري)",
  turnstile_verifying: "جارٍ التحقق…",
  turnstile_error: "فشل التحقق، يرجى المحاولة مرة أخرى.",
  metric_faceLengthToWidth: "طول/عرض الوجه",
  metric_mouthToNoseWidth: "عرض الفم/الأنف",
  metric_upperToLowerLip: "الشفة العليا/السفلى",
  metric_intercanthalRatio: "توازن تباعد العينين",
  metric_facialThirds: "أثلاث الوجه",
  metric_facialFifths: "أخماس الوجه",
  metric_symmetry: "التماثل",
  metric_redness: "الاحمرار",
  metric_texture: "النعومة",
  metric_shine: "اللمعان/الدهون",
  metric_evenness: "تجانس لون البشرة",

  // الخطوة 2 (2026-09-04): تدفق رفع 3 صور داخل الفم.
  intraoral_consent_title: "قبل أن تبدأ",
  intraoral_consent_body:
    "سيتم رفع صورك بشكل آمن واستخدامها فقط لأغراض التقييم. ليس تشخيصًا طبيًا. في وضع الكاميرا المباشرة، تتم معالجة صورتك على جهازك فقط؛ ويُرسل إلى الخادم فقط الإطار الذي توافق عليه.",
  intraoral_consent_checkbox: "لقد قرأت الإشعار أعلاه وأوافق على معالجة صوري.",
  intraoral_consent_continue: "متابعة",

  // الخطوة 6a (2026-09-04): وضع الكاميرا المباشرة.
  live_start: "استخدام الكاميرا",
  live_cancel: "إلغاء",
  live_switch_to_file: "اختيار من ملف",
  live_switch_camera: "تبديل الكاميرا",
  live_captured: "تم التقاط الصورة",
  live_hint_wrong_angle: "اضبط الكاميرا على الزاوية الصحيحة",
  live_hint_low_confidence: "اجعل الزاوية أوضح",
  live_hint_blurry: "ثبّت الكاميرا، الصورة غير واضحة",
  live_hint_dark: "انتقل إلى مكان أكثر إضاءة",
  live_hint_ready: "رائع، حافظ على الثبات…",
  live_error_permission: "تم رفض إذن الكاميرا",
  live_error_no_camera: "لم يتم العثور على كاميرا",
  live_error_model: "الوضع المباشر غير متاح حاليًا",
  live_privacy_note: "تتم معالجة صورتك على جهازك؛ يُرسل فقط الإطار الذي توافق عليه.",
  live_loading: "جارٍ التحميل…",
  slot_front_smile: "منظر أمامي أثناء الابتسام",
  slot_upper_occlusal: "الفك العلوي، السطح الماضغ",
  slot_lower_occlusal: "الفك السفلي، السطح الماضغ",
  hint_front_smile: "انظر مباشرة إلى الكاميرا وأنت تبتسم، بحيث تظهر جميع أسنانك.",
  hint_upper_occlusal: "افتح فمك ووجّه السطح الماضغ لأسنانك العلوية نحو الكاميرا. قرّب الكاميرا جيدًا من فمك.",
  hint_lower_occlusal: "افتح فمك ووجّه السطح الماضغ لأسنانك السفلية نحو الكاميرا. قرّب الكاميرا جيدًا من فمك.",
  live_tip_flip_phone: "قد يسهّل قلب الهاتف رأسًا على عقب تقريب الكاميرا من فمك.",
  live_tip_more_light: "قد يُظلّل الفك العلوي المنطقة السفلية — إذا لم تكن الإضاءة كافية، اجعل الغرفة أكثر إضاءة أو اطلب من شخص تقريب مصدر ضوء إضافي.",
  slot_example_pending: "الصورة التوضيحية قريبًا",
  slot_choose_photo: "اختيار / التقاط صورة",
  slot_retake: "إعادة الالتقاط",
  slot_status_blurry: "غير واضحة، يرجى إعادة الالتقاط",
  slot_status_dark: "الإضاءة غير كافية",
  slot_status_ok: "الصورة جيدة",
  slot_status_forced: "جارٍ المتابعة بهذه الصورة — قد تكون الجودة منخفضة",
  slot_status_unreadable: "تعذّر قراءة هذا الملف، يرجى تجربة JPEG/PNG",
  slot_use_anyway: "استخدام هذه الصورة رغم ذلك",
  slots_progress: "صورة",
  slots_continue: "متابعة",
  slots_placeholder_next: "صورك جاهزة. خطوة الرفع قريبًا.",
  uploading_title: "جارٍ رفع الصور…",
  uploading_progress: "تم الرفع",
  uploading_failed: "فشل الرفع",
  uploading_retry: "إعادة المحاولة",
  uploading_error_all: "تعذّر الاتصال. يرجى التحقق من اتصالك بالإنترنت والمحاولة مرة أخرى.",
  uploading_done: "تم رفع الصور. خطوة النموذج قريبًا.",
};

// Export edilir: scripts/sanity-i18n.ts gibi test script'leri her 6 dilin
// tam anahtar setine sahip olduğunu doğrudan (mirror-copy olmadan) kontrol edebilsin.
export const TABLES: Record<Lang, Dict> = { tr: TR, en: EN, fr: FR, de: DE, ru: RU, ar: AR };

let current: Lang = DEFAULT_LANG;

export function isRtl(l: Lang = current): boolean {
  return l === "ar";
}

export function setLang(l: Lang) {
  current = l;
  document.documentElement.lang = l;
  document.documentElement.dir = isRtl(l) ? "rtl" : "ltr";
}
export function getLang(): Lang {
  return current;
}
export function t(key: string): string {
  return TABLES[current][key] ?? TABLES.en[key] ?? key;
}

/** Adım id'sine göre rehber metni. */
export function stepHint(id: string): string {
  return t(`hint_${id}`);
}

/** Metrik anahtarına göre görünen ad (mevcut dilde). */
export function metricLabel(key: string): string {
  return t(`metric_${key}`);
}
